import React, { useContext, useState, useRef } from 'react';
import styled from 'styled-components';
import { Combobox } from '@bynder/design-system';

import { useTranslate } from '@bynder/localization';
import { IconArrowRight, IconCaretDown } from '@bynder/icons';
import { InfoContext } from '../../context';

import { API } from '../../helpers/api';
import { useOutsideClick } from '../../helpers/useOutsideClick';
import { UserAccount } from '../../types';
import { Can, AbilityContext } from '../../abilities/Can';
import { UUIDToColdfusionID } from '../../helpers/id';
import { iconSizeMedium } from '../../helpers/iconSizes';

const ACCOUNT_PATH = '/account/switch/?id=';

export function AccountSwitcher() {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [userAccounts, setUserAccounts] = useState<UserAccount[]>([]);
	const refDropdown = useRef<HTMLDivElement>(null);
	const ability = useContext(AbilityContext);
	const { translate } = useTranslate();

	useOutsideClick(refDropdown, () => {
		setIsDropdownOpen(false);
	});

	const infoContext = useContext(InfoContext);
	const { general } = infoContext;

	const onClickDropdown = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsDropdownOpen((prev: boolean) => !prev);
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			setIsDropdownOpen((prev: boolean) => !prev);
		}
	};

	const onSelectChange = async (value: string) => {
		setInputValue(value);

		if (value.trim() === '') {
			setUserAccounts([]);

			return;
		}

		await API.getAccounts(value).then((results) => {
			setUserAccounts(results);
		});
	};

	const onSelectAccount = (account: UserAccount) => {
		if (!account) {
			return;
		}

		window.location.href = ACCOUNT_PATH + account.id;
	};

	return (
		<>
			<Can I="superadmin" a="all">
				<SelectContainer>
					<Combobox
						openOn="focus"
						placeholder={general.accountName}
						value={inputValue}
						onChange={onSelectChange}
						isAnyValueAllowed
					>
						{userAccounts.length > 0 ? (
							userAccounts.map((account) => (
								<Combobox.Item
									key={account.id}
									onClick={() => onSelectAccount(account)}
								>
									{account.label}
								</Combobox.Item>
							))
						) : (
							<Combobox.Text>{translate('noresults')}</Combobox.Text>
						)}
					</Combobox>
				</SelectContainer>
			</Can>

			{!ability.can('superadmin', 'all') && infoContext.accounts.length > 1 && (
				<div
					className={`unit-switch-container ${isDropdownOpen ? 'show' : ''}`}
					ref={refDropdown}
				>
					<a
						className="unit-dropdown clearfix"
						onClick={onClickDropdown}
						onKeyDown={onKeyDown}
						role="button"
						tabIndex={0}
					>
						{general.accountName}{' '}
						<CaretIcon>
							<IconCaretDown size={iconSizeMedium} />
						</CaretIcon>
					</a>

					{isDropdownOpen && (
						<div className="unit-switch-dropdown">
							<ul className="unit-switch-list">
								{infoContext.accounts.map((account) => (
									<li key={account.account_id}>
										<a
											href={
												ACCOUNT_PATH +
												(account.account_id
													? UUIDToColdfusionID(account.account_id)
													: '') +
												'&module=&currentEvent=account.dashboard'
											}
										>
											{account.label}{' '}
											<span>
												<IconArrowRight size={iconSizeMedium} />
											</span>
										</a>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			)}
		</>
	);
}

const CaretIcon = styled.span`
	margin-left: 15px;
	float: right;
`;

const SelectContainer = styled.div`
	position: absolute;
	margin: 7px 0 0 0;
	z-index: 120;

	& input {
		width: 232px;
		height: 32px !important;
	}
`;
